import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import RichTextEditor from "../../components/forms/RichTextEditor";
import { Save, X, ChevronDown } from "lucide-react";

interface AddNewArticleProps {}

interface FormData {
  articleInfo: string;
  articleType: string;
  department: string;
  mainAuthor: string;
  publisher: string;
  authorAffiliation: string;
  coAuthorAffiliation: string;
  impactFactor: string;
  doi: string;
  researchField: string;
  keywords: string;
  authorCount: string;
  journalCategory: string;
  authorRole: string;
  month: string;
  year: string;
  taskProduct: string;
  awards: string;
  publicationLink: string;
  description: string;
  fullContent: string;
  attachedFile: File | null;
  additionalFiles: File[];
  saveToHistory: boolean;
}

// Custom Dropdown Component
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  required?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (option: string) => {
    onChange(option === options[0] ? "" : option);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
      >
        <span className={value ? "text-gray-900" : "text-gray-500"}>
          {value || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {options.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelect(option)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                index === 0 ? "text-gray-500" : "text-gray-900"
              } ${value === option ? "bg-blue-50 text-blue-700" : ""}`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AddNewArticle: React.FC<AddNewArticleProps> = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("article-info");
  const [formData, setFormData] = useState<FormData>({
    articleInfo: "",
    articleType: "",
    department: "",
    mainAuthor: "",
    publisher: "",
    authorAffiliation: "",
    coAuthorAffiliation: "",
    impactFactor: "",
    doi: "",
    researchField: "",
    keywords: "",
    authorCount: "",
    journalCategory: "",
    authorRole: "",
    month: "",
    year: "",
    taskProduct: "",
    awards: "",
    publicationLink: "",
    description: "",
    fullContent: "",
    attachedFile: null,
    additionalFiles: [],
    saveToHistory: false,
  });

  const handleInputChange = (
    field: string,
    value: string | boolean | File | File[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleInputChange("attachedFile", file);
  };

  const handleAdditionalFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      handleInputChange("additionalFiles", [
        ...formData.additionalFiles,
        ...newFiles,
      ]);
    }
  };

  const removeAdditionalFile = (index: number) => {
    const updatedFiles = formData.additionalFiles.filter((_, i) => i !== index);
    handleInputChange("additionalFiles", updatedFiles);
  };

  const handleSave = () => {
    console.log("Saving article data:", formData);
    // TODO: Implement save functionality
    navigate("/articles");
  };

  const handleCancel = () => {
    navigate("/articles");
  };

  // Mock data
  const articleTypes = [
    "- Chọn loại -",
    "Bài báo khoa học",
    "Công trình nghiên cứu",
    "Báo cáo hội nghị",
    "Chương sách",
  ];

  const departments = [
    "- Chọn Đơn vị/Phòng ban -",
    "Ban Lãnh đạo",
    "Phòng Khoa học Công nghệ",
    "Phòng Tài chính Kế toán",
    "Trung tâm Thí nghiệm",
    "Phòng Tổ chức Hành chính",
  ];

  const researchFields = [
    "- Chọn lĩnh vực nghiên cứu -",
    "Khoa học tự nhiên",
    "Công nghệ thông tin",
    "Kỹ thuật",
    "Y học",
    "Nông nghiệp",
  ];

  const journalCategories = [
    "- Danh mục của tạp chí -",
    "ISI/SCI",
    "Scopus",
    "Trong nước",
    "Quốc tế khác",
  ];

  const countries = [
    "- Chọn quốc gia -",
    "Việt Nam",
    "Hoa Kỳ",
    "Anh",
    "Nhật Bản",
    "Hàn Quốc",
    "Singapore",
  ];

  const months = [
    "- Chọn tháng -",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
  ];

  const years = [
    "- Chọn năm -",
    "2024",
    "2023",
    "2022",
    "2021",
    "2020",
    "2019",
    "2018",
    "2017",
    "2016",
    "2015",
  ];

  const mainAuthors = [
    "- Chọn tác giả chính -",
    "Nguyễn Văn A",
    "Trần Thị B",
    "Lê Văn C",
    "Phạm Thị D",
    "Hoàng Văn E",
    "Nguyễn Thị F",
    "Trần Văn G",
    "Lê Thị H",
    "Phạm Văn I",
    "Hoàng Thị J",
    "Nguyễn Văn K",
    "Trần Thị L",
    "Lê Văn M",
    "Phạm Thị N",
    "Hoàng Văn O",
    "Nguyễn Thị P",
    "Trần Văn Q",
    "Lê Thị R",
    "Phạm Văn S",
    "Hoàng Thị T",
  ];

  return (
    <PageShell title="Thêm mới Bài báo">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Thêm mới Bài báo
          </h1>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("article-info")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "article-info"
                    ? "border-[#337ab7] text-[#337ab7]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Thông tin bài báo, Công trình khoa học
              </button>
              <button
                onClick={() => setActiveTab("full-content")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "full-content"
                    ? "border-[#337ab7] text-[#337ab7]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Nội dung đầy đủ
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "article-info" && (
              <div className="space-y-6">
                {/* Top Field - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thông tin bài báo, Công trình khoa học{" "}
                    <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.articleInfo}
                    onChange={(e) =>
                      handleInputChange("articleInfo", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập thông tin bài báo..."
                  />
                </div>

                {/* Row 1 - Three columns with Tác giả chính spanning 2 columns */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loại <span className="text-red-500">(*)</span>
                    </label>
                    <CustomDropdown
                      value={formData.articleType}
                      onChange={(value) =>
                        handleInputChange("articleType", value)
                      }
                      options={articleTypes}
                      placeholder="- Chọn loại -"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đơn vị/Phòng ban <span className="text-red-500">(*)</span>
                    </label>
                    <CustomDropdown
                      value={formData.department}
                      onChange={(value) =>
                        handleInputChange("department", value)
                      }
                      options={departments}
                      placeholder="- Chọn Đơn vị/Phòng ban -"
                      required
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tác giả chính: <span className="text-red-500">(*)</span>
                    </label>
                    <CustomDropdown
                      value={formData.mainAuthor}
                      onChange={(value) =>
                        handleInputChange("mainAuthor", value)
                      }
                      options={mainAuthors}
                      placeholder="- Chọn tác giả chính -"
                      required
                    />
                  </div>
                </div>

                {/* Row 2 - Three columns with Nhà xuất bản spanning 2 columns */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trực thuộc
                    </label>
                    <CustomDropdown
                      value={formData.authorRole}
                      onChange={(value) =>
                        handleInputChange("authorRole", value)
                      }
                      options={[
                        "- Chọn -",
                        "Tác giả chính",
                        "Tác giả liên hệ",
                        "Đồng tác giả",
                      ]}
                      placeholder="- Chọn -"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thuộc quốc gia
                    </label>
                    <CustomDropdown
                      value=""
                      onChange={(value) =>
                        console.log("Country selected:", value)
                      }
                      options={countries}
                      placeholder="- Chọn quốc gia -"
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nhà xuất bản / tạp chí:
                    </label>
                    <input
                      type="text"
                      value={formData.publisher}
                      onChange={(e) =>
                        handleInputChange("publisher", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Row 3 - Two columns */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đồng tác giả thuộc trung tâm
                    </label>
                    <input
                      type="text"
                      value={formData.coAuthorAffiliation}
                      onChange={(e) =>
                        handleInputChange("coAuthorAffiliation", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nhập cách nhau bằng dấu ;"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đồng tác giả ngoài trung tâm
                    </label>
                    <input
                      type="text"
                      value={formData.authorAffiliation}
                      onChange={(e) =>
                        handleInputChange("authorAffiliation", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nhập cách nhau bằng dấu ;"
                    />
                  </div>
                </div>

                {/* Row 4 - Four columns with Month and Year combined */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Số tác giả
                    </label>
                    <input
                      type="text"
                      value={formData.authorCount}
                      onChange={(e) =>
                        handleInputChange("authorCount", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DOI
                    </label>
                    <input
                      type="text"
                      value={formData.doi}
                      onChange={(e) => handleInputChange("doi", e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IF
                    </label>
                    <input
                      type="text"
                      value={formData.impactFactor}
                      onChange={(e) =>
                        handleInputChange("impactFactor", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tháng <span className="text-red-500">(*)</span>
                        </label>
                        <CustomDropdown
                          value={formData.month}
                          onChange={(value) =>
                            handleInputChange("month", value)
                          }
                          options={months}
                          placeholder="- Chọn tháng -"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Năm xuất bản <span className="text-red-500">(*)</span>
                        </label>
                        <CustomDropdown
                          value={formData.year}
                          onChange={(value) => handleInputChange("year", value)}
                          options={years}
                          placeholder="- Chọn năm -"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5 - Four columns */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Danh mục của tạp chí
                    </label>
                    <CustomDropdown
                      value={formData.journalCategory}
                      onChange={(value) =>
                        handleInputChange("journalCategory", value)
                      }
                      options={journalCategories}
                      placeholder="- Danh mục của tạp chí -"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chuyên ngành (lĩnh vực nghiên cứu){" "}
                      <span className="text-red-500">(*)</span>
                    </label>
                    <CustomDropdown
                      value={formData.researchField}
                      onChange={(value) =>
                        handleInputChange("researchField", value)
                      }
                      options={researchFields}
                      placeholder="- Chọn lĩnh vực nghiên cứu -"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Từ khóa:
                    </label>
                    <input
                      type="text"
                      value={formData.keywords}
                      onChange={(e) =>
                        handleInputChange("keywords", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minh chứng kèm:
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Row 6 - Three columns with Link công bố spanning 2 columns */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SP của nhiệm vụ:
                    </label>
                    <input
                      type="text"
                      value={formData.taskProduct}
                      onChange={(e) =>
                        handleInputChange("taskProduct", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Khen thưởng:
                    </label>
                    <input
                      type="text"
                      value={formData.awards}
                      onChange={(e) =>
                        handleInputChange("awards", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      defaultValue="0"
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link công bố:
                    </label>
                    <input
                      type="url"
                      value={formData.publicationLink}
                      onChange={(e) =>
                        handleInputChange("publicationLink", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* Row 7 - Full width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tóm tắt nội dung
                  </label>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Nhập tóm tắt nội dung..."
                  />
                </div>

                {/* File Upload */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File đính kèm
                    </label>

                    {/* Custom File Input */}
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        id="article-file-upload"
                      />
                      <label
                        htmlFor="article-file-upload"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                      >
                        Choose File
                      </label>
                      <span className="text-sm text-gray-500">
                        {formData.attachedFile
                          ? "1 file chosen"
                          : "No file chosen"}
                      </span>
                    </div>

                    {/* File List */}
                    {formData.attachedFile && (
                      <div className="mt-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {formData.attachedFile.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(formData.attachedFile.size / 1024).toFixed(
                                    1
                                  )}{" "}
                                  KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleInputChange("attachedFile", null)
                              }
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Checkbox - Highlighted */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="saveToHistory"
                      checked={formData.saveToHistory}
                      onChange={(e) =>
                        handleInputChange("saveToHistory", e.target.checked)
                      }
                      className="w-4 h-4 rounded border-2 border-blue-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mr-3"
                    />
                    <label
                      htmlFor="saveToHistory"
                      className="text-sm font-medium text-blue-800 cursor-pointer"
                    >
                      ✓ Lưu vào lí lịch?
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 mt-1 ml-7">
                    Thông tin này sẽ được lưu vào hồ sơ cá nhân của bạn
                  </p>
                </div>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nội dung đầy đủ:
                  </label>
                  <RichTextEditor
                    value={formData.fullContent}
                    onChange={(value: string) =>
                      handleInputChange("fullContent", value)
                    }
                    placeholder="Nhập nội dung đầy đủ của bài báo, công trình khoa học..."
                    height="large"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default AddNewArticle;
